<template>
  <BaseRender
    class="department-field-mapping"
    showDefaultValue
    :loading="loading"
    :fieldTypeInfo="fieldTypeInfo"
    :fieldInfo="fieldInfo"
    :mappingList="mappingList"
    :jobCustomField="jobCustomField"
    @save="handleSave"
    @jobCustomFieldChange="handleJobCustomFieldChange"
  >
    <template #defaultValue="{ refName }">
      <el-input
        v-model="defaultValue"
        :ref="refName"
        :placeholder="$t(`${i18nPrefix}.placeholder.defaultValue`)"
        @focus="clearError(refName)"
      ></el-input>
    </template>
    <template #selfFieldRender="{ data }">
      <span v-if="jobCustomField.field" class="job-custom-field-name">{{ data.i18nName }}</span>
      <span v-else class="self-company-name" :style="{ paddingLeft: `${ 15 * (data.level - 1)}px` }">{{ data.i18nName }}</span>
    </template>
    <template #otherFieldRender="{ data, refName }">
      <el-input
        v-model="data.value"
        class="other-department-name"
        :ref="refName"
        :placeholder="$t(`${i18nPrefix}.placeholder.inputFieldMappingValue`, { channelName, fieldTypeName })"
        @focus="clearError(refName)"
      ></el-input>
    </template>
  </BaseRender>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import channelId from '../channelId'
import mixin from '../mixin'
import { addI18nProperty } from '@/i18n'

export default {
  name: `channel-${channelId.liepin}-fieldType-department`,
  mixins: [mixin],
  data () {
    return {
      mappingList: [],
      companyChildrenTree: [],
      jobCustomField: {
        show: true,
        fieldId: null,
        field: null,
        fieldList: []
      }
    }
  },
  computed: {
    departmentField () {
      return this.fieldInfo.department
    },
    defaultValue: {
      get () {
        return this.departmentField?.default
      },
      set (value) {
        if (this.departmentField) {
          this.departmentField.default = value
        }
      }
    }
  },
  created () {
    this.refreshPageData()
  },
  methods: {
    ...mapActions('company', ['getCompanyChildrenTree']),
    ...mapActions('jobCustomField', ['searchJobCustomField']),
    getDataFromApi () {
      return Promise.all([
        this.getCompanyChildrenTree().then(resp => {
          if (resp) {
            this.companyChildrenTree = resp
          }
        }),
        this.searchJobCustomField({ status: 1, type: 'SELECT', need_options: true }).then(resp => {
          if (resp) {
            _.forEach(resp, item => addI18nProperty(item, 'i18nName', 'cn_name', 'name'))
            this.jobCustomField.fieldList = resp
            if (this.jobCustomField.fieldId) {
              this.jobCustomField.field = _.find(this.jobCustomField.fieldList, { id: this.jobCustomField.fieldId })
            }
          }
        })
      ])
    },
    async refreshPageData (noNeedApiData) {
      this.loading = true
      try {
        const departmentField = this.departmentField
        let isJobCustomField = false
        if (departmentField.data_source === 'CUSTOM_FIELD') {
          const fieldId = departmentField.data_key_path
          this.jobCustomField.fieldId = parseInt(fieldId)
          isJobCustomField = true
        }
        if (!noNeedApiData) {
          await this.getDataFromApi()
        }
        const dataMapList = departmentField.data_map_list
        const dataMapKeyMap = {}
        _.forEach(dataMapList, item => {
          dataMapKeyMap[item.key] = item
        })

        if (isJobCustomField) {
          this.mappingList = _.map(this.jobCustomField.field.options, (item, index) => {
            addI18nProperty(item, 'i18nName', 'cn_name', 'name')
            const dataMap = dataMapKeyMap[item.id]
            if (dataMap) {
              dataMap.key = parseInt(dataMap.key)
              dataMap.value = parseInt(dataMap.value)
            }
            return {
              id: index + 1,
              self: item,
              other: dataMap || { key: item.id, value: null }
            }
          })
        } else {
          const companyChildrenTree = this.companyChildrenTree
          const selfCompany = companyChildrenTree.self
          const selfDataMap = dataMapKeyMap[`${selfCompany.id}`] || { key: `${selfCompany.id}`, value: null }
          const companyTree = companyChildrenTree.children
          addI18nProperty(selfCompany, 'i18nName', 'cn_name', 'name')
          const mappingList = [{
            id: 1,
            self: selfCompany,
            other: selfDataMap
          }]
          const children = companyTree[selfCompany.id]
          if (children && children.length > 0) {
            this.formatCompanyData(mappingList, dataMapKeyMap, companyTree, children)
          }
          this.mappingList = mappingList
        }
      } finally {
        this.loading = false
      }
    },
    handleJobCustomFieldChange (fieldId) {
      if (fieldId) {
        this.mappingList = _.map(this.jobCustomField.field.options, (item, index) => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'name')
          return {
            id: index + 1,
            self: item,
            other: { key: item.id, value: null }
          }
        })
      } else {
        const companyChildrenTree = this.companyChildrenTree
        const selfCompany = companyChildrenTree.self
        const companyTree = companyChildrenTree.children
        addI18nProperty(selfCompany, 'i18nName', 'cn_name', 'name')
        const mappingList = [{
          id: 1,
          self: selfCompany,
          other: { key: `${selfCompany.id}`, value: null }
        }]
        const children = companyTree[selfCompany.id]
        if (children && children.length > 0) {
          this.formatCompanyData(mappingList, {}, companyTree, children)
        }
        this.mappingList = mappingList
      }
    },
    handleSave () {
      if (!this.defaultValue) {
        this.setError(['defaultValue'])
        return
      }
      const departmentField = this.departmentField
      const isJobCustomField = Boolean(this.jobCustomField.fieldId)
      const dataMapList = []
      _.forEach(this.mappingList, item => {
        const otherField = item.other
        const selfField = item.self
        if (!otherField.value) {
          return
        }
        dataMapList.push({
          ...otherField,
          key: selfField.id
        })
      })
      departmentField.data_map_list = dataMapList
      if (isJobCustomField) {
        departmentField.data_source = 'CUSTOM_FIELD'
        departmentField.data_key_path = `${this.jobCustomField.fieldId}`
      } else {
        departmentField.data_source = 'COMPANY'
        departmentField.data_key_path = 'id'
      }
      this.$emit('save')
    }
  }
}
</script>

<style lang="scss" scoped>
.department-field-mapping {
  ::v-deep {
    .el-table {
      .el-table__body {
        .self-field {
          text-align: left;

          .cell {
            padding-right: 5px;
          }
        }

        .other-field {
          .other-company-name {
            .el-input__inner {
              padding: 0 8px;
            }
          }
        }
      }
    }
  }
}
</style>
